import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://clarities.app';

export const SEOHead = ({
  title,
  description,
  url,
  image,
  type = 'website',
  keywords,
  author,
  noIndex = false,
}) => {
  const fullTitle = title ? `${title} | Clarities` : 'Clarities - Collaborative Mindmaps';
  const defaultDescription = 'Tired of endless threads? Clarities turns crowded discussions into structured, visual mindmaps that everyone can understand and contribute to.';
  const finalDescription = description || defaultDescription;
  const canonicalUrl = url || BASE_URL;
  const ogImage = image || `${BASE_URL}/og-image.png`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Clarities" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional Meta Tags */}
      <meta name="application-name" content="Clarities" />
      <meta name="theme-color" content="#f97316" />
    </Helmet>
  );
};

// Structured data for mindmaps (JSON-LD)
export const MindmapStructuredData = ({ question, nodes, url }) => {
  if (!question) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": question.title,
    "description": question.description || `Collaborative mindmap: ${question.title}`,
    "author": {
      "@type": "Person",
      "name": question.author_name
    },
    "datePublished": question.created_at,
    "dateModified": question.updated_at,
    "publisher": {
      "@type": "Organization",
      "name": "Clarities",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "interactionStatistic": [
      {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/CommentAction",
        "userInteractionCount": nodes?.length || 0
      },
      {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/ContributeAction",
        "userInteractionCount": question.contributor_count || 0
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
